<script setup lang="ts">
const route = useRoute()
const user = useSupabaseUser()

interface NavLink {
  label: string
  description: string
  icon: string
  to: string
}

interface NavItem {
  key: 'features' | 'pricing' | 'help'
  label: string
  to: string
  active: boolean
  children?: readonly NavLink[]
  /** Last link in the dropdown, e.g. "All features" */
  footer?: { label: string, to: string }
}

const featureLinks: NavLink[] = FEATURE_LINKS.map(f => ({
  label: f.label,
  description: f.description,
  icon: f.icon,
  to: featurePath(f.slug)
}))

const items = computed<NavItem[]>(() => [{
  // Features hub; skins and the leaderboard live here too (and in the footer)
  key: 'features',
  label: 'Features',
  to: '/features',
  active: route.path.startsWith('/features') || route.path.startsWith('/skins') || route.path === '/leaderboard',
  children: featureLinks,
  footer: { label: 'All features', to: '/features' }
}, {
  key: 'pricing',
  label: 'Pricing',
  to: '/pricing',
  active: route.path === '/pricing'
}, {
  // Help center hub: docs and the suggestions board
  key: 'help',
  label: 'Help',
  to: '/help',
  active: route.path === '/help' || route.path.startsWith('/docs') || route.path.startsWith('/suggestions'),
  children: HELP_CENTER_LINKS,
  footer: { label: 'Help center', to: '/help' }
}])

/** Active state for a dropdown link: help links match their whole section (/docs, /suggestions), feature links their own page. */
function isLinkActive(item: NavItem, to: string) {
  if (item.key === 'help') return route.path.startsWith(`/${to.split('/')[1]}`)
  return route.path === to
}

// Dropdowns open on hover, while clicking the label itself goes to its hub.
// reactive() unwraps each menu's `open` ref so the template can v-model it.
const menus = {
  features: reactive(useHoverMenu()),
  help: reactive(useHoverMenu())
}
const menu = (key: NavItem['key']) => menus[key as keyof typeof menus]

const dropdownContent = {
  side: 'bottom' as const,
  align: 'start' as const,
  sideOffset: 10,
  onOpenAutoFocus: (e: Event) => e.preventDefault(),
  onCloseAutoFocus: (e: Event) => e.preventDefault()
}

const isLoggedIn = computed(() => !!user.value?.sub)
const mobileOpen = ref(false)

// Transparent over the hero, solid with a bottom border once scrolled
const scrolled = ref(false)

function onScroll() {
  scrolled.value = window.scrollY > 8
}

onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <header
    class="fixed inset-x-0 top-0 z-50 transition-colors duration-200"
    :class="scrolled || mobileOpen
      ? 'bg-(--ui-bg)/85 backdrop-blur-md border-b border-default'
      : 'bg-transparent border-b border-transparent'"
  >
    <nav class="mx-auto flex h-16 w-full max-w-(--ui-container) items-center gap-2 px-4 sm:px-6 lg:px-8">
      <!-- Logo -->
      <NuxtLink
        to="/"
        class="shrink-0"
        aria-label="mychar.gg home"
      >
        <AppLogo class="h-5 w-auto" />
      </NuxtLink>

      <!-- Nav links (desktop). From lg up: the Features dropdown is ~54rem wide -->
      <div class="ml-8 hidden items-center gap-1 lg:flex">
        <template
          v-for="item in items"
          :key="item.label"
        >
          <UPopover
            v-if="item.children"
            v-model:open="menu(item.key).open"
            :content="dropdownContent"
            :ui="{ content: 'rounded-xl' }"
          >
            <template #anchor>
              <NuxtLink
                :to="item.to"
                class="flex items-center gap-1 rounded-md px-3 py-2 text-sm transition-colors duration-200"
                :class="item.active || menu(item.key).open
                  ? 'text-highlighted'
                  : 'text-muted hover:text-highlighted'"
                @pointerenter="menu(item.key).onPointerEnter($event)"
                @pointerleave="menu(item.key).onPointerLeave($event)"
                @click="menu(item.key).close()"
              >
                {{ item.label }}
                <UIcon
                  name="i-lucide-chevron-down"
                  class="size-3.5 transition-transform duration-200"
                  :class="{ 'rotate-180': menu(item.key).open }"
                />
              </NuxtLink>
            </template>

            <template #content>
              <div
                class="p-2"
                :class="item.key === 'features' ? 'w-[54rem]' : 'w-[34rem]'"
                @pointerenter="menu(item.key).onPointerEnter($event)"
                @pointerleave="menu(item.key).onPointerLeave($event)"
              >
                <div
                  class="grid gap-1"
                  :class="item.key === 'features' ? 'grid-cols-3' : 'grid-cols-2'"
                >
                  <NuxtLink
                    v-for="link in item.children"
                    :key="link.to"
                    :to="link.to"
                    class="flex gap-3 rounded-lg p-3 transition-colors hover:bg-elevated"
                    @click="menu(item.key).close()"
                  >
                    <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <UIcon
                        :name="link.icon"
                        class="size-5 text-primary"
                      />
                    </span>
                    <span class="min-w-0">
                      <span class="block text-sm font-semibold text-highlighted">{{ link.label }}</span>
                      <span class="mt-0.5 block text-sm text-muted">{{ link.description }}</span>
                    </span>
                  </NuxtLink>
                </div>

                <NuxtLink
                  v-if="item.footer"
                  :to="item.footer.to"
                  class="mt-1 flex items-center justify-between rounded-lg border-t border-default px-3 pb-1 pt-3 text-sm font-medium text-primary transition-colors hover:text-highlighted"
                  @click="menu(item.key).close()"
                >
                  {{ item.footer.label }}
                  <UIcon
                    name="i-lucide-arrow-right"
                    class="size-4"
                  />
                </NuxtLink>
              </div>
            </template>
          </UPopover>

          <NuxtLink
            v-else
            :to="item.to"
            class="rounded-md px-3 py-2 text-sm transition-colors duration-200"
            :class="item.active
              ? 'text-highlighted'
              : 'text-muted hover:text-highlighted'"
          >
            {{ item.label }}
          </NuxtLink>
        </template>
      </div>

      <div class="flex-1" />

      <!-- Right side buttons -->
      <div class="flex items-center gap-2">
        <!-- Desktop: logged in -->
        <UButton
          v-if="isLoggedIn"
          label="Dashboard"
          color="primary"
          variant="solid"
          size="sm"
          to="/dashboard"
          class="hidden lg:inline-flex"
        />

        <!-- Desktop: not logged in -->
        <template v-if="!isLoggedIn">
          <NuxtLink
            to="/login"
            class="hidden rounded-md px-3 py-2 text-sm text-muted transition-colors duration-200 hover:text-highlighted lg:inline-flex"
          >
            Sign in
          </NuxtLink>

          <UButton
            label="Create your page"
            color="primary"
            variant="solid"
            size="sm"
            to="/signup"
            class="hidden lg:inline-flex"
          />
        </template>

        <!-- Mobile menu button -->
        <button
          class="rounded-md p-2 text-muted transition-colors hover:text-highlighted lg:hidden"
          aria-label="Toggle menu"
          :aria-expanded="mobileOpen"
          @click="mobileOpen = !mobileOpen"
        >
          <UIcon
            :name="mobileOpen ? 'i-lucide-x' : 'i-lucide-menu'"
            class="size-5"
          />
        </button>
      </div>
    </nav>

    <!-- Mobile dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <!-- Scrolls on its own (the header is fixed), so the sign in buttons stay reachable -->
      <div
        v-if="mobileOpen"
        class="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-b border-default bg-(--ui-bg)/95 px-4 pb-4 pt-1 backdrop-blur-md lg:hidden"
      >
        <div class="flex flex-col gap-1">
          <template
            v-for="item in items"
            :key="item.label"
          >
            <!-- No hover on touch: dropdowns become collapsed sections -->
            <UCollapsible
              v-if="item.children"
              class="flex flex-col gap-1"
            >
              <template #default="{ open }">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors"
                  :class="item.active
                    ? 'text-highlighted bg-elevated/70'
                    : 'text-muted hover:text-highlighted hover:bg-elevated/50'"
                >
                  {{ item.label }}
                  <UIcon
                    name="i-lucide-chevron-down"
                    class="size-4 transition-transform duration-200"
                    :class="{ 'rotate-180': open }"
                  />
                </button>
              </template>

              <template #content>
                <div class="flex flex-col gap-1">
                  <NuxtLink
                    v-for="link in item.children"
                    :key="link.to"
                    :to="link.to"
                    class="ml-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
                    :class="isLinkActive(item, link.to)
                      ? 'text-highlighted bg-elevated/70'
                      : 'text-muted hover:text-highlighted hover:bg-elevated/50'"
                    @click="mobileOpen = false"
                  >
                    <UIcon
                      :name="link.icon"
                      class="size-4 shrink-0"
                    />
                    {{ link.label }}
                  </NuxtLink>

                  <NuxtLink
                    v-if="item.footer"
                    :to="item.footer.to"
                    class="ml-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-elevated/50"
                    @click="mobileOpen = false"
                  >
                    {{ item.footer.label }}
                    <UIcon
                      name="i-lucide-arrow-right"
                      class="size-4"
                    />
                  </NuxtLink>
                </div>
              </template>
            </UCollapsible>

            <NuxtLink
              v-else
              :to="item.to"
              class="rounded-lg px-3 py-2.5 text-sm transition-colors"
              :class="item.active
                ? 'text-highlighted bg-elevated/70'
                : 'text-muted hover:text-highlighted hover:bg-elevated/50'"
              @click="mobileOpen = false"
            >
              {{ item.label }}
            </NuxtLink>
          </template>

          <div class="my-2 border-t border-default" />

          <template v-if="isLoggedIn">
            <UButton
              label="Dashboard"
              color="primary"
              variant="solid"
              to="/dashboard"
              block
              @click="mobileOpen = false"
            />
          </template>

          <template v-else>
            <NuxtLink
              to="/login"
              class="rounded-lg px-3 py-2.5 text-sm text-muted transition-colors hover:bg-elevated/50 hover:text-highlighted"
              @click="mobileOpen = false"
            >
              Sign in
            </NuxtLink>

            <UButton
              label="Create your page"
              color="primary"
              variant="solid"
              to="/signup"
              block
              class="mt-1"
              @click="mobileOpen = false"
            />
          </template>
        </div>
      </div>
    </Transition>
  </header>
</template>
